import { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'app/hooks';
import { clearSession, refreshApToken } from 'services/session/sessionAsync';
import Spin from 'components/Spin';
import If from 'components/If';
import { setInStorage, logErrors } from 'utils';
import { STORAGE_KEYS } from 'utils/constants';

const InactivityModalComponent = lazy(() => import('./inactivity-modal'));

const inactivityTime = 5 * 60 * 1000;
const countDownPeriod = 120;

function InactivityModal() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [countdown, setCountdown] = useState(countDownPeriod);
  const inactivityTimerRef = useRef<number | null>(null);
  const countdownTimerRef = useRef<number | null>(null);
  const lastActivityTimeRef = useRef<Date | null>(new Date());

  const handleSignOut = async () => {
    setInStorage(STORAGE_KEYS.inactivity, true);
    try {
      await dispatch(clearSession());
    } catch (error) {
      logErrors(error);
    } finally {
      navigate('/sign-in');
    }
  };

  const handleContinue = async () => {
    setShowModal(false);
    try {
      await dispatch(refreshApToken());
    } catch (error) {
      logErrors(error);
      handleSignOut();
    }
  };

  const resetInactivityTimer = () => {
    if (!showModal) {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      lastActivityTimeRef.current = new Date();
      inactivityTimerRef.current = window.setTimeout(() => {
        if (document.hidden) {
          handleSignOut();
        } else {
          setShowModal(true);
        }
      }, inactivityTime);
    }
  };

  const resetCountdown = () => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    setCountdown(countDownPeriod);
    countdownTimerRef.current = window.setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown <= 1 || document.hidden) {
          handleSignOut();
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
          }
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);
  };

  const handleUserActivity = () => {
    resetInactivityTimer();
  };

  const handleVisibilityChange = () => {
    if (!document.hidden) {
      const now = new Date();
      const elapsed =
        now.getTime() -
        (lastActivityTimeRef.current?.getTime() || now.getTime());
      if (elapsed >= inactivityTime) {
        handleSignOut();
      } else {
        resetInactivityTimer();
      }
    }
  };

  useEffect(() => {
    resetInactivityTimer();

    document.addEventListener('mousemove', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      document.removeEventListener('mousemove', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showModal]);

  useEffect(() => {
    if (showModal) {
      resetCountdown();
    } else if (countdownTimerRef.current)
      clearInterval(countdownTimerRef.current);
  }, [showModal]);

  return (
    <If condition={showModal}>
      <Suspense fallback={<Spin fullscreen />}>
        <InactivityModalComponent
          showModal={showModal}
          countdown={countdown}
          handleContinue={handleContinue}
          handleSignOut={handleSignOut}
        />
      </Suspense>
    </If>
  );
}

export default InactivityModal;
